import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Target, Scale, Brain, Building, Users, TrendingUp, Layers, Globe, Landmark } from 'lucide-react';

// Logo component
const Logo = ({ className = "h-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_smartgrid-hub-3/artifacts/huzue581_Nexintell%20logo.png"
    alt="Nexintell" 
    className={`${className} object-contain`}
  />
);

const commitments = [
  {
    number: "1",
    title: "We Commit to Infrastructure Intelligence Over Visibility",
    intro: "We will not reduce infrastructure to maps, dashboards, or surface-level analytics.",
    points: [
      "System reasoning over asset visualisation",
      "Long-term outcomes over short-term metrics",
      "Decision quality over data quantity"
    ],
    closing: "If a system cannot explain why an outcome occurs, it is not intelligence.",
    icon: Brain
  },
  {
    number: "2",
    title: "We Commit to Neutrality and Independence",
    intro: "Nexintell operates as an independent infrastructure intelligence layer.",
    subIntro: "We do not advocate for:",
    points: [
      "Specific vendors",
      "Particular operators",
      "Political positions",
      "Pre-determined investment outcomes"
    ],
    closing: "Our systems are designed to surface trade-offs, risks, and consequences — not to prescribe winners. Neutrality is essential for trust in multi-stakeholder, regulated, and public-interest environments.",
    icon: Scale
  },
  {
    number: "3",
    title: "We Commit to Explainable, Decision-Grade Systems",
    intro: "Infrastructure decisions shape economies, access, and resilience.",
    subIntro: "We therefore commit to building intelligence systems that are:",
    points: [
      "Explainable, not opaque",
      "Auditable, not black-box",
      "Trustworthy for public-sector and regulated environments"
    ],
    closing: "AI is a tool — not a substitute for accountability.",
    icon: Shield
  },
  {
    number: "4",
    title: "We Commit to Governance as a First-Class Design Constraint",
    intro: "Infrastructure does not exist outside governance.",
    subIntro: "We design Infralytics-GIIS with governance embedded — not bolted on — recognising:",
    points: [
      "Regulatory obligations",
      "Public accountability",
      "Multi-stakeholder decision environments"
    ],
    closing: "Infrastructure intelligence without governance is risk amplification.",
    icon: Building
  },
  {
    number: "5",
    title: "We Commit to Equity as a System Property, Not a Metric",
    intro: "Digital inequality is rarely accidental — it is systemic.",
    subIntro: "We commit to making exclusion, under-service, and structural imbalance:",
    points: [
      "Visible",
      "Measurable",
      "Decision-relevant"
    ],
    closing: "Equity must be reasoned about at system level, not reported after the fact.",
    icon: Users
  },
  {
    number: "6",
    title: "We Commit to Long-Term Infrastructure Thinking",
    intro: "We reject short-term optimisation that undermines resilience.",
    subIntro: "Our systems are designed to help institutions:",
    points: [
      "Model second- and third-order effects",
      "Understand trade-offs across time",
      "Align infrastructure decisions with long-term societal outcomes"
    ],
    closing: "Infrastructure that societies rely on must be built with foresight, not urgency alone.",
    icon: TrendingUp
  },
  {
    number: "7",
    title: "We Commit to Category Integrity",
    intro: "Nexintell is not:",
    points: [
      "A mapping company",
      "A dashboard provider",
      "A consultancy rebranded as software"
    ],
    closing: "We are building the infrastructure intelligence layer. We will protect the clarity of this category — even when doing so slows growth or limits short-term opportunities.",
    icon: Layers
  },
  {
    number: "8",
    title: "We Commit to Responsible Global Application",
    intro: "Although founded in the UK, the systems we build operate at global scale.",
    subIntro: "We commit to:",
    points: [
      "Respecting local governance and sovereignty",
      "Avoiding extractive or opaque data practices",
      "Designing intelligence systems that support institutional autonomy"
    ],
    closing: "Global infrastructure intelligence must be globally responsible.",
    icon: Globe
  },
  {
    number: "9",
    title: "We Commit to Building for Institutions, Not Just Markets",
    intro: "Markets move quickly. Institutions endure.",
    subIntro: "We design Nexintell and Infralytics-GIIS to serve:",
    points: [
      "Governments",
      "Cities",
      "Operators",
      "Investors",
      "Infrastructure stewards"
    ],
    closing: "Because the most consequential infrastructure decisions demand neutrality, discipline, and long-term trust — not short-term incentives.",
    icon: Landmark
  }
];

const CommitmentCard = ({ commitment, index }) => {
  const Icon = commitment.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="glass-panel rounded-2xl p-6 md:p-8"
      data-testid={`commitment-${commitment.number}`}
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#ff4b2a] text-white font-bold text-xl flex-shrink-0">
          {commitment.number}
        </div>
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl font-semibold text-white leading-tight">
            {commitment.title}
          </h3>
        </div>
        <Icon className="h-8 w-8 text-[#ff4b2a] flex-shrink-0 hidden md:block" />
      </div>
      
      <div className="space-y-4 text-[#a1a1a1]">
        <p className="text-lg leading-relaxed">{commitment.intro}</p>
        
        {commitment.subIntro && (
          <p className="text-base">{commitment.subIntro}</p>
        )}
        
        <ul className="space-y-2 pl-4">
          {commitment.points.map((point) => (
            <li key={point} className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#ff4b2a] flex-shrink-0" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
        
        <p className="text-white font-medium pt-2 border-t border-white/10">
          {commitment.closing}
        </p>
      </div>
    </motion.div>
  );
};

export default function Commitments() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="commitments-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#0a0a0a]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="flex items-center" data-testid="logo-link">
              <Logo className="h-8 md:h-10" />
            </a>
            <a 
              href="/" 
              className="flex items-center gap-2 text-[#a1a1a1] hover:text-white transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 relative">
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-mono uppercase tracking-widest text-[#ff4b2a] mb-4">
              Our Principles
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="commitments-title">
              Our Commitments
            </h1>
            <p className="text-lg md:text-xl text-[#a1a1a1] leading-relaxed max-w-3xl mx-auto">
              At Nexintell, we are building infrastructure intelligence as a foundational capability 
              for how societies plan, govern, and invest in digital systems.
            </p>
            <p className="text-lg md:text-xl text-white font-medium mt-4">
              To do that responsibly, we commit to the following principles.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Commitments Grid */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto space-y-6">
          {commitments.map((commitment, index) => (
            <CommitmentCard key={commitment.number} commitment={commitment} index={index} />
          ))}
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 px-6 md:px-12 relative">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-panel rounded-3xl p-8 md:p-12"
          >
            <div className="w-16 h-16 rounded-full bg-[#ff4b2a]/20 flex items-center justify-center mx-auto mb-6">
              <Target className="h-8 w-8 text-[#ff4b2a]" />
            </div>
            <p className="text-xl md:text-2xl lg:text-3xl text-white font-medium leading-relaxed">
              Nexintell exists to ensure that the digital infrastructure societies depend on is not just deployed — but understood, governed, and improved intelligently over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-semibold mb-6">Get in Touch</h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 text-[#a1a1a1]">
            <a 
              href="mailto:contact@nexintell.co" 
              className="flex items-center gap-2 hover:text-[#ff4b2a] transition-colors"
              data-testid="contact-email"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              contact@nexintell.co
            </a>
            <a 
              href="tel:+447867038150" 
              className="flex items-center gap-2 hover:text-[#ff4b2a] transition-colors"
              data-testid="contact-phone"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              +44 7867 038150
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <Logo className="h-6" />
          </div>
          <div className="flex items-center gap-6 text-sm text-[#a1a1a1]">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/commitments" className="hover:text-white transition-colors text-[#ff4b2a]">Commitments</a>
          </div>
          <div className="text-sm text-[#a1a1a1]">
            © {new Date().getFullYear()} Nexintell Ltd. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
